import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";

import ModalWrapper from "components/common/modals/modalWrapper/ModalWrapper";
import { useSelector } from "react-redux";
import formatDate from "utils/formatDate";
import { ALERT_ACTION_TEXT } from "static/constants";

const SignProcessDataReviewModal = ({
  show,
  onClose,
  onIgnoreAndContinue,
  isProcessing,
  title = "Review Reports Before Sign & Process",
  action = "sign-and-process",
}) => {
  const { selectedReports, reportList } = useSelector(
    (state) => state.transmission
  );

  // Filter to only show nurse_flag reports from selectedReports
  const nurseFlagReports = useMemo(() => {
    return (
      selectedReports?.filter((reportId) => {
        const report = reportList?.find((r) => r._id === reportId);
        return report?.nurse_flag;
      }) || []
    );
  }, [selectedReports, reportList]);

  const filteredReportList = useMemo(() => {
    return (
      reportList?.filter((report) => nurseFlagReports?.includes(report._id)) ||
      []
    );
  }, [reportList, nurseFlagReports]);

  const hasAnyNurseFlag = filteredReportList?.some((report) => {
    return report?.nurse_flag;
  });

  const handleIgnoreAndContinue = () => {
    onIgnoreAndContinue();
  };

  const getAlertMessage = () => {
    const actionText = ALERT_ACTION_TEXT[action];

    if (!actionText) {
      return null;
    }

    return (
      <>
        Some of the selected reports are <strong>Nurse Flagged</strong> and
        cannot be {actionText}. These reports will be ignored, and all other
        selected reports will be {actionText}.
      </>
    );
  };

  return (
    <ModalWrapper
      show={show}
      onClose={onClose}
      title={title}
      maxWidth="md"
      actionButtons={[
        {
          label: "Cancel",
          variant: "text",
          color: "inherit",
          onClick: onClose,
          disabled: isProcessing,
        },
        {
          label: isProcessing ? "Processing" : "Ignore and continue",
          variant: "contained",
          color: "secondary",
          onClick: handleIgnoreAndContinue,
          disabled: nurseFlagReports?.length === 0 || isProcessing,
          isLoading: isProcessing,
        },
      ]}
    >
      <div style={{ paddingTop: "16px" }}>
        {
          <>
            {hasAnyNurseFlag && (
              <Alert
                severity="warning"
                icon={
                  <WarningIcon
                    sx={{
                      width: 24,
                      height: 24,
                      color: "#FF9800",
                    }}
                  />
                }
                style={{
                  marginBottom: "16px",
                  backgroundColor: "#FFF4E5",
                  border: "1px solid #FF9800",
                  color: "#FF9800",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    fontFamily: "Roboto",
                    fontWeight: 400,
                    fontStyle: "bold",
                    fontSize: "16px",
                    lineHeight: 1.5,
                    letterSpacing: "0%",
                  }}
                >
                  {getAlertMessage()}
                </div>
              </Alert>
            )}

            <TableContainer
              style={{
                maxHeight: "400px",
                overflowY: "auto",
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Report ID</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Report Date</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Patient Name</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredReportList?.map((report) => (
                    <TableRow key={report._id}>
                      <TableCell
                        style={{
                          fontSize: "12px",
                          maxWidth: "100px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={report._id}
                      >
                        {report._id}
                      </TableCell>
                      <TableCell style={{ fontSize: "12px" }}>
                        {report?.report_date
                          ? formatDate(new Date(report?.report_date))
                          : "N/A"}
                      </TableCell>
                      <TableCell style={{ fontSize: "12px" }}>
                        {report?.name || "N/A"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        }
      </div>
    </ModalWrapper>
  );
};

export default SignProcessDataReviewModal;
